import React, { useState, useEffect } from 'react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { MessageSquare, Plus, Check, AlertCircle, Edit, Trash2, X, Target, ChevronDown, ChevronUp, Users, Copy, ExternalLink } from 'lucide-react'
import { toast } from 'sonner'
import { supabase } from '@/integrations/supabase/client'
import { useClientTeamMembers } from '@/hooks/useClientTeamMembers'
import { useAuth } from '@/contexts/AuthContext'
import { useQueryClient } from '@tanstack/react-query'
import { SlackChannelMemberSelector } from '@/components/clients/slack/SlackChannelMemberSelector'
import { createClientChannels, createSingleChannel } from '@/lib/slack/channel-service'
import type { TemplateVariables } from '@/lib/slack/messages/welcome-messages'
import type { ClientSlackForm, SlackChannelConfig, Territory } from '@/types/client'
import type { Client } from '@/hooks/useClients'

interface ClientSlackStepProps {
  data: ClientSlackForm
  onChange: (data: Partial<ClientSlackForm>) => void
  isLoading?: boolean
  client?: Client | null
  clientId?: string
  clientName?: string
  productData?: { products: any[], territories: Territory[] }
  teamMembers?: any[] // Team members from formData for immediate availability
}

// Add Channel Dialog Component (defined first)
interface AddChannelDialogProps {
  type: string
  territories: Territory[]
  onAdd: (territoryId?: string, territoryName?: string, customChannelName?: string) => void
  onCancel: () => void
  clientName?: string
}

function AddChannelDialog({ type, territories, onAdd, onCancel, clientName }: AddChannelDialogProps) {
  const [selectedTerritory, setSelectedTerritory] = useState<string>('ALL_REGIONS')
  const [customChannelName, setCustomChannelName] = useState<string>('')
  const [useCustomName, setUseCustomName] = useState<boolean>(false)
  
  // Generate suggested channel name based on selections
  const getSuggestedName = () => {
    const client = clientName || 'Client'
    const territory = selectedTerritory !== 'ALL_REGIONS' && selectedTerritory
      ? territories?.find(t => t?.id === selectedTerritory)?.name
      : null
    const territorySuffix = territory ? ` - ${territory}` : ''
    
    let channelType = ''
    switch (type) {
      case 'sales':
        channelType = 'General'
        break
      case 'positive_replies':
        channelType = 'Positive Replies'
        break
      case 'neutral_negative_replies':
        channelType = 'Negative Replies'
        break
      default:
        channelType = 'Channel'
    }
    
    return `${client} - OB - ${channelType}${territorySuffix}`
  }
  
  const handleAdd = () => {
    try {
      const channelNameToUse = useCustomName && customChannelName.trim() 
        ? customChannelName.trim()
        : getSuggestedName()
      
      if (selectedTerritory === 'ALL_REGIONS' || !selectedTerritory) {
        onAdd(undefined, undefined, channelNameToUse)
      } else {
        const territory = territories?.find(t => t?.id === selectedTerritory)
        onAdd(territory?.id, territory?.name, channelNameToUse)
      }
    } catch (error) {
      console.error('Error adding channel:', error)
      toast.error('Failed to add channel. Please try again.')
    }
  }
  
  const safeTerritories = Array.isArray(territories) ? territories.filter(t => t?.id && t?.name) : []
  
  return (
    <div className="space-y-4">
      {safeTerritories.length > 0 && (
        <div className="space-y-2">
          <Label>Territory (optional)</Label>
          <Select value={selectedTerritory} onValueChange={setSelectedTerritory}>
            <SelectTrigger>
              <SelectValue placeholder="Select territory or leave for all regions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL_REGIONS">All Regions</SelectItem>
              {safeTerritories.map(territory => (
                <SelectItem key={territory.id} value={territory.id}>
                  {territory.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Channel Name</Label>
          <button
            type="button"
            className="text-xs text-primary hover:underline"
            onClick={() => setUseCustomName(!useCustomName)}
          >
            {useCustomName ? 'Use suggested name' : 'Enter custom name'}
          </button>
        </div>
        
        {useCustomName ? (
          <Input
            value={customChannelName}
            onChange={(e) => setCustomChannelName(e.target.value)}
            placeholder="Enter existing Slack channel name"
          />
        ) : (
          <div className="p-3 bg-muted rounded-lg text-sm">
            <strong>Suggested:</strong> {getSuggestedName()}
          </div>
        )}
      </div>
      
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="button" onClick={handleAdd}>
          Add Channel
        </Button>
      </div>
    </div>
  )
}

// Add Channel Dialog Wrapper Component
interface AddChannelDialogWrapperProps {
  showAddDialog: { type: string }
  productData?: { products: any[], territories: Territory[] }
  clientName?: string
  onAddChannel: (type: string, territoryId?: string, territoryName?: string, customChannelName?: string) => void
  onClose: () => void
}

function AddChannelDialogWrapper({ showAddDialog, productData, clientName, onAddChannel, onClose }: AddChannelDialogWrapperProps) {
  const dialogType = showAddDialog.type
  const titleText = dialogType === 'sales' ? 'Sales' : 
                    dialogType === 'positive_replies' ? 'Positive Reply' : 
                    dialogType === 'neutral_negative_replies' ? 'Negative Reply' : 
                    'Channel'
  
  return (
    <Dialog 
      open={true} 
      onOpenChange={(open) => {
        if (!open) {
          onClose()
        }
      }}
    >
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Add {titleText} Channel
          </DialogTitle>
          <DialogDescription>
            Assign an existing Slack channel or create a new channel configuration
          </DialogDescription>
        </DialogHeader>
        <AddChannelDialog
          type={dialogType}
          territories={productData?.territories || []}
          clientName={clientName}
          onAdd={(territoryId, territoryName, customChannelName) => {
            onAddChannel(dialogType, territoryId, territoryName, customChannelName)
          }}
          onCancel={onClose}
        />
      </DialogContent>
    </Dialog>
  )
}

// Leadership ('main') and reporting were retired on 2026-09-02: their automation
// had been dead for months, so the wizard no longer offers them. Existing client
// channels and their columns are untouched -- this step just cannot create more.
type ChannelKey = 'sales' | 'positive' | 'neutralNegative' | 'meetingsBooked' | 'websiteIdentification'

// Created together by "Create Standard Channels".
const DEFAULT_CHANNEL_KEYS: ChannelKey[] = ['sales', 'positive', 'neutralNegative']
const CHANNEL_LABELS: Record<ChannelKey, string> = {
  sales: 'General',
  positive: 'Positive Replies',
  // Named just "negative": neutral replies now route to positive (Jamie,
  // 2026-09-02). The key and DB column keep the old neutralNegative naming.
  neutralNegative: 'Negative Replies',
  meetingsBooked: 'Meetings Booked',
  websiteIdentification: 'Website Identification'
}

const CHANNEL_NAME_FIELDS: Record<ChannelKey, keyof ClientSlackForm> = {
  sales: 'slack_sales_channel',
  positive: 'slack_positive_replies_channel',
  neutralNegative: 'slack_neutral_negative_replies_channel',
  meetingsBooked: 'slack_meetings_booked_channel',
  websiteIdentification: 'slack_website_identification_channel'
}

// OutboundBuddy members added to every client channel, plus per-channel extras.
// Mirrors OUTBOUNDBUDDY_CORE_EMAILS in the create-client-slack-channels function;
// the function is the source of truth, this copy only drives the UI's counts.
const OUTBOUNDBUDDY_CORE_EMAILS = [
  'jamie@outboundbuddy.com',
  'sabeeh@outboundbuddy.com',
  'alex@outboundbuddy.com',
  'fahad@outboundbuddy.com'
]
const OUTBOUNDBUDDY_EXTRA_EMAILS_BY_CHANNEL: Partial<Record<ChannelKey, string[]>> = {
  sales: ['darcy@outboundbuddy.com']
}
const outboundBuddyEmailsFor = (channelType: ChannelKey): string[] =>
  [...new Set([...OUTBOUNDBUDDY_CORE_EMAILS, ...(OUTBOUNDBUDDY_EXTRA_EMAILS_BY_CHANNEL[channelType] ?? [])])]

// Role-based channel assignment logic
const getChannelsForRole = (role: string): ChannelKey[] => {
  const normalizedRole = role.toLowerCase().trim()
  
  // Account Manager: sales + positive + neutral/negative
  if (normalizedRole.includes('account manager')) {
    return ['sales', 'positive', 'neutralNegative']
  }
  
  // AE: sales + positive + neutral/negative
  if (normalizedRole.includes('ae') || normalizedRole === 'account executive') {
    return ['sales', 'positive', 'neutralNegative']
  }
  
  // SDR: sales + positive + neutral/negative
  if (normalizedRole.includes('sdr') || normalizedRole === 'sales development representative') {
    return ['sales', 'positive', 'neutralNegative']
  }
  
  // Sales Manager: sales + positive + neutral/negative
  if (normalizedRole.includes('sales manager')) {
    return ['sales', 'positive', 'neutralNegative']
  }

  // Leadership (C-level, VP, Executive): positive + neutral/negative
  if (normalizedRole.includes('c-level') || normalizedRole.includes('vp') || normalizedRole.includes('executive')) {
    return ['positive', 'neutralNegative']
  }
  
  // Default: sales channel only
  return ['sales']
}

export function ClientSlackStep({ data, onChange, isLoading, client, clientId, clientName, productData, teamMembers: formDataTeamMembers }: ClientSlackStepProps) {
  const [isCreatingStandardChannels, setIsCreatingStandardChannels] = useState(false)
  const [creatingChannel, setCreatingChannel] = useState<ChannelKey | null>(null)
  const [editingChannel, setEditingChannel] = useState<{ type: string, channel: SlackChannelConfig } | null>(null)
  const [showAddDialog, setShowAddDialog] = useState<{ type: string } | null>(null)
  const [expandedChannels, setExpandedChannels] = useState<Record<string, boolean>>({})
  // Territory-based channel sections start collapsed -- most clients never use
  // them, and expanded they bury the main channel cards.
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({})
  const [channelVerification, setChannelVerification] = useState<Record<string, {
    verified: string[];
    missing: string[];
    verifiedEmails: string[];
    missingEmails: string[];
    expected: number;
    manualInvitesNeeded?: Array<{
      email: string;
      channelId: string;
      channelName: string;
      adminInviteUrl: string;
      instructions: string;
    }>;
  }>>({})
  const [verificationDialogOpen, setVerificationDialogOpen] = useState(false)
  const [selectedChannelForVerification, setSelectedChannelForVerification] = useState<{
    type: ChannelKey;
    name: string;
    verification: any;
  } | null>(null)
  const { teamMembers } = useClientTeamMembers(client?.id || '')
  const { profile } = useAuth()
  const queryClient = useQueryClient()
  
  // Resolve client info from props
  const resolvedClientId = client?.id || clientId || ''
  const resolvedClientName = client?.name || clientName || ''
  const resolvedTeamMembers = (formDataTeamMembers || teamMembers || []).filter(m => m.email)
  
  // Initialize arrays if they don't exist
  const salesChannels = data.slack_sales_channels || []
  const positiveReplyChannels = data.slack_positive_reply_channels || []
  const neutralNegativeReplyChannels = data.slack_neutral_negative_reply_channels || []
  
  // Helper to detect if a value is a Slack channel ID vs a channel name
  const isSlackChannelId = (value: string | null | undefined): boolean => {
    if (!value) return false
    // Slack channel IDs start with 'C' and are typically 9-11 characters
    // Channel names contain text like "-Main", "-Leadership", etc.
    return /^C[A-Z0-9]{8,}$/.test(value.trim())
  }

  // Helper to get display name for channel (converts ID to readable name if needed)
  const getChannelDisplayName = (channelId: string | null | undefined, channelType: ChannelKey): string => {
    if (!channelId) return ''
    
    // If it's a Slack ID, generate a readable name
    if (isSlackChannelId(channelId)) {
      const client = resolvedClientName || 'Client'
      return `${client} - OB - ${CHANNEL_LABELS[channelType]}`
    }
    
    // Otherwise return as-is (with legacy name replacements)
    return channelId.replace(' - outboundbuddy - ', ' x OB - ')
  }
  
  // Auto-populate and migrate channel names when client name is available
  useEffect(() => {
    if (client?.name) {
      const updates: Partial<ClientSlackForm> = {}
      
      // Leadership and reporting are retired: no auto-naming, so nothing writes a
      // placeholder name into slack_main_channel / slack_reporting_channel any more.
      // Existing values are left exactly as they are.

      // Migrate old "Sales" channel names to "General" and "outboundbuddy" to "OB"
      if (data.slack_sales_channel && !isSlackChannelId(data.slack_sales_channel)) {
        let updatedName = data.slack_sales_channel
        if (updatedName.includes('outboundbuddy')) {
          updatedName = updatedName.replace(/outboundbuddy/g, 'OB')
        }
        if (updatedName.includes('-Sales')) {
          updatedName = updatedName.replace(/-Sales/g, '-General')
        }
        if (updatedName !== data.slack_sales_channel) {
          updates.slack_sales_channel = updatedName
        }
      }
      
      // Migrate positive replies channel
      if (data.slack_positive_replies_channel && !isSlackChannelId(data.slack_positive_replies_channel)) {
        if (data.slack_positive_replies_channel.includes('outboundbuddy')) {
          updates.slack_positive_replies_channel = data.slack_positive_replies_channel.replace(/outboundbuddy/g, 'OB')
        }
      }
      
      // Migrate neutral/negative replies channel
      if (data.slack_neutral_negative_replies_channel && !isSlackChannelId(data.slack_neutral_negative_replies_channel)) {
        if (data.slack_neutral_negative_replies_channel.includes('outboundbuddy')) {
          updates.slack_neutral_negative_replies_channel = data.slack_neutral_negative_replies_channel.replace(/outboundbuddy/g, 'OB')
        }
      }
      
      // Migrate territory-based sales channels
      const updatedSalesChannels = salesChannels.map(channel => {
        let updatedName = channel.name
        
        // Replace "outboundbuddy" with "OB"
        if (updatedName.includes('outboundbuddy')) {
          updatedName = updatedName.replace(/outboundbuddy/g, 'OB')
        }
        
        // Replace "-Sales" with "-General" in territory channels
        if (updatedName.includes('-Sales')) {
          updatedName = updatedName.replace(/-Sales/g, '-General')
        }
        
        if (updatedName !== channel.name) {
          return { ...channel, name: updatedName }
        }
        return channel
      })
      
      if (updatedSalesChannels.some((ch, idx) => ch.name !== salesChannels[idx]?.name)) {
        updates.slack_sales_channels = updatedSalesChannels
      }
      
      // Migrate positive reply channels
      const updatedPositiveChannels = positiveReplyChannels.map(channel => {
        let updatedName = channel.name
        if (updatedName.includes('outboundbuddy')) {
          updatedName = updatedName.replace(/outboundbuddy/g, 'OB')
        }
        if (updatedName !== channel.name) {
          return { ...channel, name: updatedName }
        }
        return channel
      })
      
      if (updatedPositiveChannels.some((ch, idx) => ch.name !== positiveReplyChannels[idx]?.name)) {
        updates.slack_positive_reply_channels = updatedPositiveChannels
      }
      
      // Migrate neutral/negative reply channels
      const updatedNeutralChannels = neutralNegativeReplyChannels.map(channel => {
        let updatedName = channel.name
        if (updatedName.includes('outboundbuddy')) {
          updatedName = updatedName.replace(/outboundbuddy/g, 'OB')
        }
        if (updatedName !== channel.name) {
          return { ...channel, name: updatedName }
        }
        return channel
      })
      
      if (updatedNeutralChannels.some((ch, idx) => ch.name !== neutralNegativeReplyChannels[idx]?.name)) {
        updates.slack_neutral_negative_reply_channels = updatedNeutralChannels
      }
      
      if (Object.keys(updates).length > 0) {
        onChange(updates)
      }
    }
  }, [client?.name, data.slack_sales_channel, data.slack_positive_replies_channel, data.slack_neutral_negative_replies_channel, salesChannels, positiveReplyChannels, neutralNegativeReplyChannels])
  
  // Helper to create a new channel config
  const createNewChannel = (type: string, territoryId?: string, territoryName?: string, customChannelName?: string): SlackChannelConfig => {
    try {
      let channelName = ''
      
      // Use custom channel name if provided
      if (customChannelName && customChannelName.trim()) {
        channelName = customChannelName.trim()
      } else {
        // Generate suggested name
        const clientName = client?.name || 'Client'
        const territorySuffix = territoryName ? ` - ${territoryName}` : ''
        
        switch (type) {
          case 'sales':
            channelName = `${clientName} - OB - General${territorySuffix}`
            break
          case 'positive_replies':
            channelName = `${clientName} - OB - Positive Replies${territorySuffix}`
            break
          case 'neutral_negative_replies':
            channelName = `${clientName} - OB - Negative Replies${territorySuffix}`
            break
          default:
            channelName = `${clientName} - OB - Channel${territorySuffix}`
        }
      }
      
      return {
        id: crypto.randomUUID(),
        name: channelName,
        territory_id: territoryId || undefined,
        territory_name: territoryName || undefined,
        team_member_ids: [],
        status: 'not_set'
      }
    } catch (error) {
      console.error('Error creating new channel:', error)
      // Return a safe default channel config
      return {
        id: crypto.randomUUID(),
        name: customChannelName || `Client - OB - Channel`,
        territory_id: territoryId,
        territory_name: territoryName,
        team_member_ids: [],
        status: 'not_set'
      }
    }
  }
  
  const handleInputChange = (field: keyof ClientSlackForm, value: string | string[]) => {
    onChange({ [field]: value })
  }

  const handleAddChannel = (type: string, territoryId?: string, territoryName?: string, customChannelName?: string) => {
    try {
      const newChannel = createNewChannel(type, territoryId, territoryName, customChannelName)
      
      switch (type) {
        case 'sales':
          onChange({ slack_sales_channels: [...salesChannels, newChannel] })
          break
        case 'positive_replies':
          onChange({ slack_positive_reply_channels: [...positiveReplyChannels, newChannel] })
          break
        case 'neutral_negative_replies':
          onChange({ slack_neutral_negative_reply_channels: [...neutralNegativeReplyChannels, newChannel] })
          break
        default:
          console.warn('Unknown channel type:', type)
          return
      }
      
      setShowAddDialog(null)
      setEditingChannel({ type, channel: newChannel })
      toast.success('Channel added successfully')
    } catch (error) {
      console.error('Error in handleAddChannel:', error)
      toast.error('Failed to add channel. Please try again.')
    }
  }
  
  const handleCreateForAllTerritories = (type: string) => {
    if (!productData?.territories || productData.territories.length === 0) {
      toast.error('No territories found. Please add territories first.')
        return
      }

    const newChannels: SlackChannelConfig[] = productData.territories.map(territory => 
      createNewChannel(type, territory.id, territory.name)
    )
    
    switch (type) {
      case 'positive_replies':
        onChange({ slack_positive_reply_channels: [...positiveReplyChannels, ...newChannels] })
        break
      case 'neutral_negative_replies':
        onChange({ slack_neutral_negative_reply_channels: [...neutralNegativeReplyChannels, ...newChannels] })
        break
    }
    
    toast.success(`Created ${newChannels.length} channels for all territories`)
  }
  
  const handleUpdateChannel = (type: string, updatedChannel: SlackChannelConfig) => {
    switch (type) {
      case 'sales':
        onChange({
          slack_sales_channels: salesChannels.map(c => c.id === updatedChannel.id ? updatedChannel : c)
        })
        break
      case 'positive_replies':
        onChange({
          slack_positive_reply_channels: positiveReplyChannels.map(c => c.id === updatedChannel.id ? updatedChannel : c)
        })
        break
      case 'neutral_negative_replies':
        onChange({
          slack_neutral_negative_reply_channels: neutralNegativeReplyChannels.map(c => c.id === updatedChannel.id ? updatedChannel : c)
        })
        break
    }
    setEditingChannel(null)
  }

  const handleChannelMemberChange = (type: string, channelId: string, memberIds: string[]) => {
    const updateChannel = (channel: SlackChannelConfig) => ({
      ...channel,
      team_member_ids: memberIds
    })

    switch (type) {
      case 'sales':
        onChange({
          slack_sales_channels: salesChannels.map(c => c.id === channelId ? updateChannel(c) : c)
        })
        break
      case 'positive_replies':
        onChange({
          slack_positive_reply_channels: positiveReplyChannels.map(c => c.id === channelId ? updateChannel(c) : c)
        })
        break
      case 'neutral_negative_replies':
        onChange({
          slack_neutral_negative_reply_channels: neutralNegativeReplyChannels.map(c => c.id === channelId ? updateChannel(c) : c)
        })
        break
    }
  }

  const toggleSectionExpanded = (type: string) => {
    setExpandedSections(prev => ({ ...prev, [type]: !prev[type] }))
  }

  const toggleChannelExpanded = (channelId: string) => {
    setExpandedChannels(prev => ({
      ...prev,
      [channelId]: !prev[channelId]
    }))
  }
  
  const handleDeleteChannel = (type: string, channelId: string) => {
    if (!confirm('Are you sure you want to delete this channel?')) return
    
    switch (type) {
      case 'sales':
        onChange({ slack_sales_channels: salesChannels.filter(c => c.id !== channelId) })
        break
      case 'positive_replies':
        onChange({ slack_positive_reply_channels: positiveReplyChannels.filter(c => c.id !== channelId) })
        break
      case 'neutral_negative_replies':
        onChange({ slack_neutral_negative_reply_channels: neutralNegativeReplyChannels.filter(c => c.id !== channelId) })
        break
    }
    toast.success('Channel removed')
  }
  
  // Helper to build template variables
  const buildTemplateVariables = (): TemplateVariables => {
    const clientSlug = (name: string): string => {
      return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
    }
    
    return {
      CLIENT_NAME: resolvedClientName,
      DASHBOARD_URL: 'https://app.outboundbuddy.com',
      FUNNEL_URL: 'https://app.outboundbuddy.com/funnel',
      EMAIL_ANALYTICS_URL: 'https://app.outboundbuddy.com/analytics/email',
      LINKEDIN_ANALYTICS_URL: 'https://app.outboundbuddy.com/analytics/linkedin',
      CALENDAR_URL: 'https://calendly.com/outboundbuddy',
      PLAYBOOKS_URL: 'https://app.outboundbuddy.com/playbooks',
      CRM_LINK: 'https://app.outboundbuddy.com/crm',
      RESPONSE_PLAYBOOK_URL: 'https://app.outboundbuddy.com/playbooks/responses',
      COPYWRITING_DOC_URL: 'https://app.outboundbuddy.com/playbooks/copywriting',
      TIMEZONE: 'EST',
      AM_NAME: 'Jamie',
      SDR_LEAD: 'TBD',
      AE_OWNER: 'TBD',
      POSITIVE_REPLY_SLA_MIN: 15,
      LOOM_VIDEO_URL: 'https://www.loom.com/share/1dd9b9af0f1a485d8ee66522f3615ffb',
      SALES_CHANNEL_LINK: `#${clientSlug(resolvedClientName)}-general`,
      REPORTING_CHANNEL_LINK: `#${clientSlug(resolvedClientName)}-reporting`,
      POSITIVE_CHANNEL_LINK: `#${clientSlug(resolvedClientName)}-positive-replies`,
      NEUTRAL_CHANNEL_LINK: `#${clientSlug(resolvedClientName)}-negative-replies`
    }
  }

  // Helper to get member emails for a channel based on roles
  const getMemberEmailsForChannel = (channelType: ChannelKey): string[] => {
    const emails: string[] = [...outboundBuddyEmailsFor(channelType)]

    // Map channel type to form data field name for team member IDs
    const memberIdFields: Record<ChannelKey, keyof ClientSlackForm> = {
      sales: 'slack_sales_team_member_ids',
      positive: 'slack_positive_replies_team_member_ids',
      neutralNegative: 'slack_neutral_negative_replies_team_member_ids',
      meetingsBooked: 'slack_meetings_booked_team_member_ids',
      websiteIdentification: 'slack_website_identification_team_member_ids'
    }
    
    // Get selected member IDs from form data
    const memberIdField = memberIdFields[channelType]
    const selectedMemberIds = (data[memberIdField] as string[]) || []
    
    // Map selected member IDs to emails from resolvedTeamMembers
    if (selectedMemberIds.length > 0 && resolvedTeamMembers.length > 0) {
      selectedMemberIds.forEach(memberId => {
        const member = resolvedTeamMembers.find(m => m.id === memberId)
        if (member && member.email && !emails.includes(member.email)) {
          emails.push(member.email)
        }
      })
    }
    
    // Fallback to role-based logic if no members are explicitly selected
    // This ensures channels still get members even if form selection is empty
    if (selectedMemberIds.length === 0) {
      resolvedTeamMembers.forEach(member => {
        const channelsForRole = getChannelsForRole(member.role || '')
        if (channelsForRole.includes(channelType) && !emails.includes(member.email)) {
          emails.push(member.email)
        }
      })
    }
    
    return [...new Set(emails)]
  }

  // Create a single channel
  const handleCreateSingleChannel = async (channelType: ChannelKey) => {
    if (!resolvedClientId || !resolvedClientName) {
      toast.error('Client information is required to create Slack channel')
      return
    }

    setCreatingChannel(channelType)
    
    try {
      toast.info(`Creating ${CHANNEL_LABELS[channelType]} channel...`)
      
      const memberEmails = getMemberEmailsForChannel(channelType)
      const templateVariables = buildTemplateVariables()

      // Get channel name from form data if it exists and is not a Slack ID
      const channelNameField = CHANNEL_NAME_FIELDS[channelType]
      const suggestedChannelName = data[channelNameField] as string | undefined
      const useChannelName = suggestedChannelName && !isSlackChannelId(suggestedChannelName)
        ? suggestedChannelName
        : undefined

      const result = await createSingleChannel(
        resolvedClientId,
        resolvedClientName,
        channelType,
        {
          variables: templateVariables,
          memberEmails,
          channelName: useChannelName
        }
      )

      if (!result.success) {
        throw new Error(result.error || 'Failed to create Slack channel')
      }

      // Update form data with the created channel ID
      const updates: Partial<ClientSlackForm> = {}
      
      if (result.channel) {
        switch (channelType) {
          case 'sales':
            updates.slack_sales_channel = result.channel.id
            break
          case 'positive':
            updates.slack_positive_replies_channel = result.channel.id
            break
          case 'neutralNegative':
            updates.slack_neutral_negative_replies_channel = result.channel.id
            break
          case 'meetingsBooked':
            updates.slack_meetings_booked_channel = result.channel.id
            break
          case 'websiteIdentification':
            updates.slack_website_identification_channel = result.channel.id
            break
        }
        console.log(`✅ Channel created - updating form data:`, updates)
      } else {
        console.warn('⚠️  Channel creation succeeded but no channel data returned')
      }

      // Log the update before applying it
      console.log('📝 [ClientSlackStep] Calling onChange with updates:', updates)
      console.log('📝 [ClientSlackStep] Current form data before update:', {
        slack_sales_channel: data.slack_sales_channel,
        slack_positive_replies_channel: data.slack_positive_replies_channel,
        slack_neutral_negative_replies_channel: data.slack_neutral_negative_replies_channel,
        slack_meetings_booked_channel: data.slack_meetings_booked_channel,
        slack_website_identification_channel: data.slack_website_identification_channel
      })

      onChange(updates)
      
      // Invalidate React Query cache so client data refreshes from database
      // This ensures channel IDs persist after page refresh
      if (resolvedClientId) {
        queryClient.invalidateQueries({ queryKey: ['clients'] })
        queryClient.invalidateQueries({ queryKey: ['clients-list'] })
        console.log('🔄 Invalidated React Query cache to refresh client data')
      }
      
      // Log after onChange to verify the update was applied
      // Note: This won't show the updated value immediately since state updates are async
      setTimeout(() => {
        console.log('📝 [ClientSlackStep] Form data should be updated now. Check if re-render happened.')
      }, 100)
      
      // Store verification data if available
      if (result.verification) {
        // Debug: Log verification data to see what we're receiving
        console.log(`📊 [ClientSlackStep] Verification data for ${channelType}:`, {
          verified: result.verification.verified?.length || 0,
          missing: result.verification.missing?.length || 0,
          verifiedEmails: result.verification.verifiedEmails?.length || 0,
          missingEmails: result.verification.missingEmails?.length || 0,
          expected: result.verification.expected,
          manualInvitesNeeded: result.verification.manualInvitesNeeded?.length || 0,
          fullVerification: result.verification
        });
        
        setChannelVerification(prev => ({
          ...prev,
          [channelType]: result.verification!
        }))
        
        // Enhanced toast message with verification results
        // Check for missing emails OR manual invites needed
        const hasMissing = (result.verification.missing?.length || 0) > 0 || 
                          (result.verification.missingEmails?.length || 0) > 0 ||
                          (result.verification.manualInvitesNeeded?.length || 0) > 0;
        
        if (hasMissing) {
          toast.warning(
            `Channel created, but ${result.verification.missing.length} member(s) couldn't be added`,
            {
              description: `Failed: ${result.verification.missingEmails.slice(0, 3).join(', ')}${result.verification.missingEmails.length > 3 ? '...' : ''}`,
              duration: 8000
            }
          )
        } else {
          toast.success(
            `Successfully created ${CHANNEL_LABELS[channelType]} channel!`,
            {
              description: `Added ${result.verification.verified.length} of ${result.verification.expected} team members`,
              duration: 5000
            }
          )
        }
      } else {
        // Fallback to old message format if no verification data
        const memberInfo = result.membersAdded !== undefined 
          ? `Added ${result.membersAdded}${result.membersAttempted && result.membersAttempted > result.membersAdded ? `/${result.membersAttempted}` : ''} team members`
          : `Added ${memberEmails.length} team members`
        
        toast.success(`Successfully created ${CHANNEL_LABELS[channelType]} channel! ${memberInfo}.`)
      }
      
    } catch (error: any) {
      console.error('Error creating Slack channel:', error)
      toast.error(`Failed to create Slack channel: ${error.message || 'Unknown error'}`)
    } finally {
      setCreatingChannel(null)
    }
  }

  // Create the default channel set with welcome messages
  const handleCreateStandardSlackChannels = async () => {
    if (!resolvedClientId || !resolvedClientName) {
      toast.error('Client information is required to create Slack channels')
      return
    }

    if (!profile?.email) {
      toast.warning('Your email is not available. You will not be automatically added to the channels.')
    }

    setIsCreatingStandardChannels(true)
    
    try {
      toast.info(`Creating ${DEFAULT_CHANNEL_KEYS.length} Slack channels for ${resolvedClientName}...`)
      
      const templateVariables = buildTemplateVariables()

      // Build role-based channel assignments for the default set only. The
      // on-demand channels (meetings booked, website identification) are never
      // created here -- each has its own button.
      const channelAssignments: Partial<Record<ChannelKey, string[]>> = {}
      DEFAULT_CHANNEL_KEYS.forEach(channel => {
        // OutboundBuddy core team on every channel, plus that channel's extras.
        channelAssignments[channel] = [...outboundBuddyEmailsFor(channel)]
      })

      // Add client team members based on their roles
      resolvedTeamMembers.forEach(member => {
        const channelsForRole = getChannelsForRole(member.role || '')
        channelsForRole.forEach(channel => {
          const assigned = channelAssignments[channel]
          if (assigned && !assigned.includes(member.email)) {
            assigned.push(member.email)
          }
        })
      })

      // Remove duplicates from each channel's email list
      Object.keys(channelAssignments).forEach(channel => {
        channelAssignments[channel as ChannelKey] = [...new Set(channelAssignments[channel as ChannelKey])]
      })

      // Collect channel names from form data if they exist and are not Slack IDs.
      // Default set only -- naming an on-demand channel must not pull it into the
      // bulk create.
      const channelNames: Partial<Record<ChannelKey, string>> = {}
      DEFAULT_CHANNEL_KEYS.forEach(channelType => {
        const channelName = data[CHANNEL_NAME_FIELDS[channelType]] as string | undefined
        if (channelName && !isSlackChannelId(channelName)) {
          channelNames[channelType] = channelName
        }
      })

      // Call the channel service to create all channels with role-based assignments
      const result = await createClientChannels(
        resolvedClientId,
        resolvedClientName,
        {
          variables: templateVariables,
          channelAssignments,
          channelNames: Object.keys(channelNames).length > 0 ? channelNames : undefined
        }
      )

      if (!result.success) {
        throw new Error(result.error || 'Failed to create Slack channels')
      }

      // Update form data with the created channel IDs
      const updates: Partial<ClientSlackForm> = {}
      
      if (result.channels.sales) {
        updates.slack_sales_channel = result.channels.sales.id
      }
      if (result.channels.positive) {
        updates.slack_positive_replies_channel = result.channels.positive.id
      }
      if (result.channels.neutralNegative) {
        updates.slack_neutral_negative_replies_channel = result.channels.neutralNegative.id
      }

      // Update the form data with all channel IDs
      onChange(updates)
      
      // Invalidate React Query cache so client data refreshes from database
      // This ensures channel IDs persist after page refresh
      if (resolvedClientId) {
        queryClient.invalidateQueries({ queryKey: ['clients'] })
        queryClient.invalidateQueries({ queryKey: ['clients-list'] })
        console.log('🔄 Invalidated React Query cache to refresh client data after bulk channel creation')
      }
      
      // Calculate total unique members across all channels
      const allUniqueEmails = [...new Set(Object.values(channelAssignments).flat())]
      const totalClientMembers = resolvedTeamMembers.length
      const totalMembers = allUniqueEmails.length
      
      toast.success(
        `Successfully created ${DEFAULT_CHANNEL_KEYS.length} Slack channels! ` +
        `Added ${totalMembers} team members (${OUTBOUNDBUDDY_CORE_EMAILS.length} OutboundBuddy + ${totalClientMembers} client team) to appropriate channels based on roles.`
      )
      
    } catch (error: any) {
      console.error('Error creating Slack channels:', error)
      toast.error(`Failed to create Slack channels: ${error.message || 'Unknown error'}`)
    } finally {
      setIsCreatingStandardChannels(false)
    }
  }

  // Create a single territory-based channel
  const handleCreateSingleTerritoryChannel = async (
    channel: SlackChannelConfig,
    channelType: 'sales' | 'positive_replies' | 'neutral_negative_replies'
  ) => {
    if (!resolvedClientId || !resolvedClientName) {
      toast.error('Client information is required to create Slack channel')
      return
    }

    // Mark channel as creating
    const updatedChannel: SlackChannelConfig = {
      ...channel,
      status: 'creating'
    }
    
    // Update the channel in state immediately
    const updateChannelInState = (channels: SlackChannelConfig[], updated: SlackChannelConfig) => {
      return channels.map(c => c.id === updated.id ? updated : c)
    }
    
    const tempUpdates: Partial<ClientSlackForm> = {}
    if (channelType === 'sales') {
      tempUpdates.slack_sales_channels = updateChannelInState(salesChannels, updatedChannel)
    } else if (channelType === 'positive_replies') {
      tempUpdates.slack_positive_reply_channels = updateChannelInState(positiveReplyChannels, updatedChannel)
    } else if (channelType === 'neutral_negative_replies') {
      tempUpdates.slack_neutral_negative_reply_channels = updateChannelInState(neutralNegativeReplyChannels, updatedChannel)
    }
    onChange(tempUpdates)

    try {
      // Map UI channel type to Edge Function channel type
      let edgeChannelType: 'sales' | 'positive' | 'neutralNegative'
      
      if (channelType === 'sales') {
        edgeChannelType = 'sales' // Maps to 'general' in Edge Function
      } else if (channelType === 'positive_replies') {
        edgeChannelType = 'positive'
      } else if (channelType === 'neutral_negative_replies') {
        edgeChannelType = 'neutralNegative'
      } else {
        throw new Error(`Unknown channel type: ${channelType}`)
      }

      // Get team members for this channel
      const activeTeamMembers = teamMembers.filter(m => m.status === 'active')
      const channelMembers = activeTeamMembers.filter(m => channel.team_member_ids.includes(m.id))
      
      // ALWAYS include OutboundBuddy team members
      const outboundBuddyEmails = ['sabeeh@outboundbuddy.com', 'jamie@outboundbuddy.com']
      const allMemberEmails = [
        ...new Set([
          ...channelMembers.map(m => m.email),
          ...outboundBuddyEmails
        ])
      ]
      
      const templateVariables = buildTemplateVariables()

      const result = await createSingleChannel(
        resolvedClientId,
        resolvedClientName,
        edgeChannelType,
        {
          channelName: channel.name.replace('#', ''),
          memberEmails: allMemberEmails,
          variables: templateVariables
        }
      )

      if (!result.success) {
        throw new Error(result.error || 'Failed to create Slack channel')
      }

      // Update channel with created ID and status
      const finalUpdatedChannel: SlackChannelConfig = {
        ...channel,
        status: 'created',
        channel_id: result.channel?.id
      }
      
      // Update the channel in state
      const finalUpdates: Partial<ClientSlackForm> = {}
      if (channelType === 'sales') {
        finalUpdates.slack_sales_channels = updateChannelInState(salesChannels, finalUpdatedChannel)
      } else if (channelType === 'positive_replies') {
        finalUpdates.slack_positive_reply_channels = updateChannelInState(positiveReplyChannels, finalUpdatedChannel)
      } else if (channelType === 'neutral_negative_replies') {
        finalUpdates.slack_neutral_negative_reply_channels = updateChannelInState(neutralNegativeReplyChannels, finalUpdatedChannel)
      }
      
      onChange(finalUpdates)
      
      // Invalidate cache
      if (resolvedClientId) {
        queryClient.invalidateQueries({ queryKey: ['clients'] })
        queryClient.invalidateQueries({ queryKey: ['clients-list'] })
      }
      
      toast.success(`Successfully created channel ${channel.name}!`)
      
    } catch (error: any) {
      console.error(`Error creating territory channel ${channel.name}:`, error)
      
      // Mark channel as error
      const errorChannel: SlackChannelConfig = {
        ...channel,
        status: 'error'
      }
      
      const errorUpdates: Partial<ClientSlackForm> = {}
      if (channelType === 'sales') {
        errorUpdates.slack_sales_channels = updateChannelInState(salesChannels, errorChannel)
      } else if (channelType === 'positive_replies') {
        errorUpdates.slack_positive_reply_channels = updateChannelInState(positiveReplyChannels, errorChannel)
      } else if (channelType === 'neutral_negative_replies') {
        errorUpdates.slack_neutral_negative_reply_channels = updateChannelInState(neutralNegativeReplyChannels, errorChannel)
      }
      
      onChange(errorUpdates)
      toast.error(`Failed to create channel: ${error.message || 'Unknown error'}`)
    }
  }
  
  const getStatusIcon = (status: SlackChannelConfig['status']) => {
    switch (status) {
      case 'created':
        return <Check className="h-4 w-4 text-green-600" />
      case 'creating':
        return <div className="h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      case 'error':
        return <AlertCircle className="h-4 w-4 text-red-600" />
      default:
        return <div className="h-4 w-4 border-2 border-gray-300 rounded-full" />
    }
  }

  // Helper to render member status badge
  const renderMemberStatusBadge = (channelType: ChannelKey, channelId?: string) => {
    const verification = channelVerification[channelType]
    if (!verification || !channelId) return null
    
    const allAdded = verification.missing.length === 0
    const badgeVariant = allAdded ? "secondary" : "destructive"
    
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant={badgeVariant} className="cursor-help">
            <Users className="h-3 w-3 mr-1" />
            {verification.verified.length}/{verification.expected} Members
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <div className="space-y-1 text-sm">
            <p className="font-semibold">Member Status:</p>
            <p className="text-green-600">✅ Added: {verification.verified.length}</p>
            {verification.missing.length > 0 && (
              <>
                <p className="text-red-600">❌ Failed: {verification.missing.length}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {verification.missingEmails.join(', ')}
                </p>
              </>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    )
  }
  
  const getStatusBadge = (status: SlackChannelConfig['status']) => {
    switch (status) {
      case 'created':
        return <Badge variant="default" className="bg-green-100 text-green-800">Created</Badge>
      case 'creating':
        return <Badge variant="default" className="bg-blue-100 text-blue-800">Creating...</Badge>
      case 'error':
        return <Badge variant="destructive">Error</Badge>
      default:
        return <Badge variant="secondary">Not Set</Badge>
    }
  }
  
  const renderChannelList = (
    channels: SlackChannelConfig[],
    type: string,
    title: string,
    description: string,
    canCreateMultiple: boolean = true
  ) => {
    const isSectionExpanded = expandedSections[type] || false

    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div
              className="flex-1 cursor-pointer"
              onClick={() => toggleSectionExpanded(type)}
            >
              <CardTitle className="text-lg flex items-center gap-2">
                {title}
                {channels.length > 0 && (
                  <Badge variant="secondary" className="text-xs">{channels.length}</Badge>
                )}
              </CardTitle>
              <CardDescription>{description}</CardDescription>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => toggleSectionExpanded(type)}
              aria-expanded={isSectionExpanded}
              aria-label={isSectionExpanded ? `Collapse ${title}` : `Expand ${title}`}
            >
              {isSectionExpanded ? (
                <ChevronUp className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              )}
            </Button>
            {canCreateMultiple && isSectionExpanded && (
              <div className="flex gap-2">
                {(type === 'positive_replies' || type === 'neutral_negative_replies') && productData?.territories && productData.territories.length > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleCreateForAllTerritories(type)}
                    disabled={isLoading}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Create for All Territories
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    try {
                      setShowAddDialog({ type })
                    } catch (error) {
                      console.error('Error opening add channel dialog:', error)
                      toast.error('Failed to open dialog')
                    }
                  }}
                  disabled={isLoading}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Channel
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        {isSectionExpanded && (
        <CardContent className="space-y-3">
          {channels.length === 0 ? (
            <div className="text-sm text-muted-foreground p-4 border rounded-lg text-center">
              No channels configured. Click "Add Channel" to create one.
            </div>
          ) : (
            channels.map((channel) => {
              const isExpanded = expandedChannels[channel.id] || false
              
              return (
                <div key={channel.id} className="border rounded-lg overflow-hidden">
                  <div 
                    className="flex items-center justify-between p-3 hover:bg-muted/50 cursor-pointer transition-colors"
                    onClick={() => toggleChannelExpanded(channel.id)}
                  >
                    <div className="flex items-center space-x-3 flex-1">
                      {getStatusIcon(channel.status)}
                      <div className="flex-1">
                        <div className="font-medium flex items-center gap-2">
                          {channel.name}
                          {channel.territory_name && (
                            <Badge variant="outline" className="text-xs">
                              <Target className="h-3 w-3 mr-1" />
                              {channel.territory_name}
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground flex items-center gap-2">
                          <Users className="h-3 w-3" />
                          {channel.team_member_ids.length} team member{channel.team_member_ids.length !== 1 ? 's' : ''}
                        </div>
                      </div>
                      {getStatusBadge(channel.status)}
                    </div>
                    <div className="flex items-center space-x-2">
                      {/* Show Create Channel button if channel not created, or Channel Created badge if it is */}
                      {(() => {
                        const isChannelCreated = channel.channel_id && channel.status === 'created'
                        const isCreating = channel.status === 'creating'
                        
                        if (isChannelCreated) {
                          return (
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                              <Check className="h-3 w-3 mr-1" />
                              Channel Created
                            </Badge>
                          )
                        }
                        
                        return (
                          <Button
                            type="button"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleCreateSingleTerritoryChannel(channel, type as 'sales' | 'positive_replies' | 'neutral_negative_replies')
                            }}
                            disabled={isCreating || isLoading || !resolvedClientId || !resolvedClientName}
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            {isCreating ? 'Creating...' : 'Create Channel'}
                          </Button>
                        )
                      })()}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation()
                          setEditingChannel({ type, channel })
                        }}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDeleteChannel(type, channel.id)
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                      {isExpanded ? (
                        <ChevronUp className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      )}
                    </div>
                  </div>
                  
                  {isExpanded && client && (
                    <div className="border-t bg-muted/30 p-4 space-y-3">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Team Members</Label>
                        <SlackChannelMemberSelector
                          clientId={client.id}
                          channelType={type as any}
                          territoryId={channel.territory_id}
                          territoryName={channel.territory_name}
                          existingMemberIds={channel.team_member_ids}
                          selectedMemberIds={channel.team_member_ids}
                          onSelectionChange={(memberIds) => handleChannelMemberChange(type, channel.id, memberIds)}
                          productData={productData}
                          formDataTeamMembers={formDataTeamMembers}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )
            })
          )}
        </CardContent>
        )}
      </Card>
    )
  }
  
  return (
    <div className="space-y-6">
      {/* General Channel (formerly Sales) */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>General Channel</CardTitle>
              <CardDescription>General channel for sales/marketing team updates and requests</CardDescription>
            </div>
            {(() => {
              const isChannelCreated = isSlackChannelId(data.slack_sales_channel)
              return !isChannelCreated ? (
                <Button
                  onClick={() => handleCreateSingleChannel('sales')}
                  disabled={creatingChannel === 'sales' || isLoading || !resolvedClientId || !resolvedClientName}
                  size="sm"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {creatingChannel === 'sales' ? 'Creating...' : 'Create Channel'}
                </Button>
              ) : (
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                    <Check className="h-3 w-3 mr-1" />
                    Channel Created
                  </Badge>
                  {renderMemberStatusBadge('sales', data.slack_sales_channel || undefined)}
                  {channelVerification['sales'] && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedChannelForVerification({
                          type: 'sales',
                          name: getChannelDisplayName(data.slack_sales_channel, 'sales'),
                          verification: channelVerification['sales']
                        })
                        setVerificationDialogOpen(true)
                      }}
                    >
                      <Users className="h-4 w-4 mr-1" />
                      Verify
                    </Button>
                  )}
                </div>
              )
            })()}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800 mb-4">
            <strong>Note:</strong> {isSlackChannelId(data.slack_sales_channel) ? 'Channel created. You can also add territory-specific channels below.' : 'Click "Create Channel" to automatically create the channel with welcome message and team members, or enter the channel name manually.'}
          </div>
          <div className="space-y-2">
            <Label htmlFor="slack_sales_channel">Channel Name</Label>
            <Input
              id="slack_sales_channel"
              value={getChannelDisplayName(data.slack_sales_channel, 'sales')}
              onChange={(e) => handleInputChange('slack_sales_channel', e.target.value)}
              placeholder={client?.name ? `${client.name} - OB - General` : "ClientName - OB - General"}
              disabled={isLoading}
            />
            {isSlackChannelId(data.slack_sales_channel) && (
              <p className="text-xs text-muted-foreground mt-1">Channel ID: {data.slack_sales_channel}</p>
            )}
          </div>
          {client && (
            <div className="space-y-2">
              <Label>Team Members</Label>
              <SlackChannelMemberSelector
                key="sales-channel-selector"
                clientId={client.id}
                channelType="sales"
                existingMemberIds={data.slack_sales_team_member_ids || []}
                selectedMemberIds={data.slack_sales_team_member_ids || []}
                onSelectionChange={(memberIds) => onChange({ slack_sales_team_member_ids: memberIds })}
                productData={productData}
                formDataTeamMembers={formDataTeamMembers}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Territory-based General Channels */}
      {renderChannelList(
        salesChannels,
        'sales',
        'Territory-Based General Channels',
        'Additional general team coordination channels (region-specific)'
      )}

          {/* Positive Replies Channel */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Positive Replies Channel</CardTitle>
              <CardDescription>Channel for positive replies, meeting requests, and lead engagement</CardDescription>
            </div>
            {(() => {
              const isChannelCreated = isSlackChannelId(data.slack_positive_replies_channel)
              return !isChannelCreated ? (
                <Button
                  onClick={() => handleCreateSingleChannel('positive')}
                  disabled={creatingChannel === 'positive' || isLoading || !resolvedClientId || !resolvedClientName}
                  size="sm"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {creatingChannel === 'positive' ? 'Creating...' : 'Create Channel'}
                </Button>
              ) : (
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                    <Check className="h-3 w-3 mr-1" />
                    Channel Created
                  </Badge>
                  {renderMemberStatusBadge('positive', data.slack_positive_replies_channel || undefined)}
                  {channelVerification['positive'] && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedChannelForVerification({
                          type: 'positive',
                          name: getChannelDisplayName(data.slack_positive_replies_channel, 'positive'),
                          verification: channelVerification['positive']
                        })
                        setVerificationDialogOpen(true)
                      }}
                    >
                      <Users className="h-4 w-4 mr-1" />
                      Verify
                    </Button>
                  )}
                </div>
              )
            })()}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800 mb-4">
            <strong>Note:</strong> {isSlackChannelId(data.slack_positive_replies_channel) ? 'Channel created. You can also add territory-specific channels below.' : 'Click "Create Channel" to automatically create the channel with welcome message and team members, or enter the channel name manually.'}
          </div>
          <div className="space-y-2">
            <Label htmlFor="slack_positive_replies_channel">Channel Name</Label>
            <Input
              id="slack_positive_replies_channel"
              value={getChannelDisplayName(data.slack_positive_replies_channel, 'positive')}
              onChange={(e) => handleInputChange('slack_positive_replies_channel', e.target.value)}
              placeholder={client?.name ? `${client.name} - OB - Positive Replies` : "ClientName - OB - Positive Replies"}
              disabled={isLoading}
            />
            {isSlackChannelId(data.slack_positive_replies_channel) && (
              <p className="text-xs text-muted-foreground mt-1">Channel ID: {data.slack_positive_replies_channel}</p>
            )}
          </div>
          {client && (
            <div className="space-y-2">
              <Label>Team Members</Label>
              <SlackChannelMemberSelector
                key="positive-channel-selector"
                clientId={client.id}
                channelType="positive"
                existingMemberIds={data.slack_positive_replies_team_member_ids || []}
                selectedMemberIds={data.slack_positive_replies_team_member_ids || []}
                onSelectionChange={(memberIds) => onChange({ slack_positive_replies_team_member_ids: memberIds })}
                productData={productData}
                formDataTeamMembers={formDataTeamMembers}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Territory-based Positive Reply Channels */}
      {renderChannelList(
        positiveReplyChannels,
        'positive_replies',
        'Territory-Based Positive Reply Channels',
        'Additional positive reply channels (region-specific)'
      )}

          {/* Negative Replies Channel */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Negative Replies Channel</CardTitle>
              <CardDescription>Channel for negative reply notifications</CardDescription>
            </div>
            {(() => {
              const isChannelCreated = isSlackChannelId(data.slack_neutral_negative_replies_channel)
              return !isChannelCreated ? (
                <Button
                  onClick={() => handleCreateSingleChannel('neutralNegative')}
                  disabled={creatingChannel === 'neutralNegative' || isLoading || !resolvedClientId || !resolvedClientName}
                  size="sm"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {creatingChannel === 'neutralNegative' ? 'Creating...' : 'Create Channel'}
                </Button>
              ) : (
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                    <Check className="h-3 w-3 mr-1" />
                    Channel Created
                  </Badge>
                  {renderMemberStatusBadge('neutralNegative', data.slack_neutral_negative_replies_channel || undefined)}
                  {channelVerification['neutralNegative'] && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedChannelForVerification({
                          type: 'neutralNegative',
                          name: getChannelDisplayName(data.slack_neutral_negative_replies_channel, 'neutralNegative'),
                          verification: channelVerification['neutralNegative']
                        })
                        setVerificationDialogOpen(true)
                      }}
                    >
                      <Users className="h-4 w-4 mr-1" />
                      Verify
                    </Button>
                  )}
                </div>
              )
            })()}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800 mb-4">
            <strong>Note:</strong> {isSlackChannelId(data.slack_neutral_negative_replies_channel) ? 'Channel created. You can also add territory-specific channels below.' : 'Click "Create Channel" to automatically create the channel with welcome message and team members, or enter the channel name manually.'}
          </div>
          <div className="space-y-2">
            <Label htmlFor="slack_neutral_negative_replies_channel">Channel Name</Label>
            <Input
              id="slack_neutral_negative_replies_channel"
              value={getChannelDisplayName(data.slack_neutral_negative_replies_channel, 'neutralNegative')}
              onChange={(e) => handleInputChange('slack_neutral_negative_replies_channel', e.target.value)}
              placeholder={client?.name ? `${client.name} - OB - Negative Replies` : "ClientName - OB - Negative Replies"}
              disabled={isLoading}
            />
            {isSlackChannelId(data.slack_neutral_negative_replies_channel) && (
              <p className="text-xs text-muted-foreground mt-1">Channel ID: {data.slack_neutral_negative_replies_channel}</p>
            )}
          </div>
          {client && (
            <div className="space-y-2">
              <Label>Team Members</Label>
              <SlackChannelMemberSelector
                key="neutral-negative-channel-selector"
                clientId={client.id}
                channelType="neutral_negative"
                existingMemberIds={data.slack_neutral_negative_replies_team_member_ids || []}
                selectedMemberIds={data.slack_neutral_negative_replies_team_member_ids || []}
                onSelectionChange={(memberIds) => onChange({ slack_neutral_negative_replies_team_member_ids: memberIds })}
                productData={productData}
                formDataTeamMembers={formDataTeamMembers}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Territory-based Negative Reply Channels */}
      {renderChannelList(
        neutralNegativeReplyChannels,
        'neutral_negative_replies',
        'Territory-Based Negative Reply Channels',
        'Additional negative reply channels (region-specific)'
      )}
      

      {/* Meetings Booked Channel (on demand) */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Meetings Booked Channel</CardTitle>
              <CardDescription>Alerts when a meeting is booked off outbound, with the campaign and reply it came from</CardDescription>
            </div>
            {(() => {
              const isChannelCreated = isSlackChannelId(data.slack_meetings_booked_channel)
              return !isChannelCreated ? (
                <Button
                  onClick={() => handleCreateSingleChannel('meetingsBooked')}
                  disabled={creatingChannel === 'meetingsBooked' || isLoading || !resolvedClientId || !resolvedClientName}
                  size="sm"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {creatingChannel === 'meetingsBooked' ? 'Creating...' : 'Create Channel'}
                </Button>
              ) : (
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                    <Check className="h-3 w-3 mr-1" />
                    Channel Created
                  </Badge>
                  {renderMemberStatusBadge('meetingsBooked', data.slack_meetings_booked_channel || undefined)}
                  {channelVerification['meetingsBooked'] && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedChannelForVerification({
                          type: 'meetingsBooked',
                          name: getChannelDisplayName(data.slack_meetings_booked_channel, 'meetingsBooked'),
                          verification: channelVerification['meetingsBooked']
                        })
                        setVerificationDialogOpen(true)
                      }}
                    >
                      <Users className="h-4 w-4 mr-1" />
                      Verify
                    </Button>
                  )}
                </div>
              )
            })()}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 mb-4">
            <strong>On demand:</strong> Not created with the standard channels. Click "Create Channel" only when this client needs it - hubspot-meetings-sync reads the channel from here once it exists.
          </div>
          <div className="space-y-2">
            <Label htmlFor="slack_meetings_booked_channel">Channel Name</Label>
            <Input
              id="slack_meetings_booked_channel"
              value={getChannelDisplayName(data.slack_meetings_booked_channel, 'meetingsBooked')}
              onChange={(e) => handleInputChange('slack_meetings_booked_channel', e.target.value)}
              placeholder={client?.name ? `${client.name} - OB - Meetings Booked` : "ClientName - OB - Meetings Booked"}
              disabled={isLoading}
            />
            {isSlackChannelId(data.slack_meetings_booked_channel) && (
              <p className="text-xs text-muted-foreground mt-1">Channel ID: {data.slack_meetings_booked_channel}</p>
            )}
          </div>
          {client && (
            <div className="space-y-2">
              <Label>Team Members</Label>
              <SlackChannelMemberSelector
                key="meetings-booked-channel-selector"
                clientId={client.id}
                channelType="meetings_booked"
                existingMemberIds={data.slack_meetings_booked_team_member_ids || []}
                selectedMemberIds={data.slack_meetings_booked_team_member_ids || []}
                onSelectionChange={(memberIds) => onChange({ slack_meetings_booked_team_member_ids: memberIds })}
                productData={productData}
                formDataTeamMembers={formDataTeamMembers}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Website Identification Channel (on demand) */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Website Identification Channel</CardTitle>
              <CardDescription>Alerts when a company from the target list is identified visiting the website</CardDescription>
            </div>
            {(() => {
              const isChannelCreated = isSlackChannelId(data.slack_website_identification_channel)
              return !isChannelCreated ? (
                <Button
                  onClick={() => handleCreateSingleChannel('websiteIdentification')}
                  disabled={creatingChannel === 'websiteIdentification' || isLoading || !resolvedClientId || !resolvedClientName}
                  size="sm"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {creatingChannel === 'websiteIdentification' ? 'Creating...' : 'Create Channel'}
                </Button>
              ) : (
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                    <Check className="h-3 w-3 mr-1" />
                    Channel Created
                  </Badge>
                  {renderMemberStatusBadge('websiteIdentification', data.slack_website_identification_channel || undefined)}
                  {channelVerification['websiteIdentification'] && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedChannelForVerification({
                          type: 'websiteIdentification',
                          name: getChannelDisplayName(data.slack_website_identification_channel, 'websiteIdentification'),
                          verification: channelVerification['websiteIdentification']
                        })
                        setVerificationDialogOpen(true)
                      }}
                    >
                      <Users className="h-4 w-4 mr-1" />
                      Verify
                    </Button>
                  )}
                </div>
              )
            })()}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 mb-4">
            <strong>On demand:</strong> Not created with the standard channels. Click "Create Channel" only when this client needs it.
          </div>
          <div className="space-y-2">
            <Label htmlFor="slack_website_identification_channel">Channel Name</Label>
            <Input
              id="slack_website_identification_channel"
              value={getChannelDisplayName(data.slack_website_identification_channel, 'websiteIdentification')}
              onChange={(e) => handleInputChange('slack_website_identification_channel', e.target.value)}
              placeholder={client?.name ? `${client.name} - OB - Website Identification` : "ClientName - OB - Website Identification"}
              disabled={isLoading}
            />
            {isSlackChannelId(data.slack_website_identification_channel) && (
              <p className="text-xs text-muted-foreground mt-1">Channel ID: {data.slack_website_identification_channel}</p>
            )}
          </div>
          {client && (
            <div className="space-y-2">
              <Label>Team Members</Label>
              <SlackChannelMemberSelector
                key="website-identification-channel-selector"
                clientId={client.id}
                channelType="website_identification"
                existingMemberIds={data.slack_website_identification_team_member_ids || []}
                selectedMemberIds={data.slack_website_identification_team_member_ids || []}
                onSelectionChange={(memberIds) => onChange({ slack_website_identification_team_member_ids: memberIds })}
                productData={productData}
                formDataTeamMembers={formDataTeamMembers}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Verification Dialog */}
      {selectedChannelForVerification && (
        <Dialog open={verificationDialogOpen} onOpenChange={setVerificationDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Member Verification: {selectedChannelForVerification.name}</DialogTitle>
              <DialogDescription>
                Verify which team members were successfully added to the channel
              </DialogDescription>
            </DialogHeader>
            
            {/* Debug: Log verification data when dialog opens */}
            {(() => {
              console.log('🔍 [VerificationDialog] Verification data:', {
                verification: selectedChannelForVerification.verification,
                missing: selectedChannelForVerification.verification.missing?.length || 0,
                missingEmails: selectedChannelForVerification.verification.missingEmails?.length || 0,
                manualInvitesNeeded: selectedChannelForVerification.verification.manualInvitesNeeded?.length || 0,
                fullData: JSON.stringify(selectedChannelForVerification.verification, null, 2)
              });
              return null;
            })()}
            
            <div className="space-y-4">
              {/* Summary */}
              {(() => {
                const verifiedCount = selectedChannelForVerification.verification.verified?.length || 0;
                const missingCount = Math.max(
                  selectedChannelForVerification.verification.missing?.length || 0,
                  selectedChannelForVerification.verification.missingEmails?.length || 0,
                  selectedChannelForVerification.verification.manualInvitesNeeded?.length || 0
                );
                const expectedCount = selectedChannelForVerification.verification.expected || 
                  (verifiedCount + missingCount);
                
                return (
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 bg-green-50 rounded-lg">
                      <div className="text-2xl font-bold text-green-700">{verifiedCount}</div>
                      <div className="text-sm text-green-600">Successfully Added</div>
                    </div>
                    <div className="p-4 bg-red-50 rounded-lg">
                      <div className="text-2xl font-bold text-red-700">{missingCount}</div>
                      <div className="text-sm text-red-600">Failed to Add</div>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <div className="text-2xl font-bold text-blue-700">{expectedCount}</div>
                      <div className="text-sm text-blue-600">Total Expected</div>
                    </div>
                  </div>
                );
              })()}
              
              {/* Verified Members */}
              {selectedChannelForVerification.verification.verified.length > 0 && (
                <div>
                  <h4 className="font-semibold text-green-700 mb-2 flex items-center gap-2">
                    <Check className="h-4 w-4" />
                    Successfully Added ({selectedChannelForVerification.verification.verified.length})
                  </h4>
                  <div className="space-y-1">
                    {selectedChannelForVerification.verification.verifiedEmails.map(email => (
                      <div key={email} className="flex items-center gap-2 text-sm p-2 bg-green-50 rounded">
                        <Check className="h-3 w-3 text-green-600" />
                        {email}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Missing Members - Show if there are missing emails OR manual invites needed */}
              {((selectedChannelForVerification.verification.missing?.length || 0) > 0 || 
                (selectedChannelForVerification.verification.missingEmails?.length || 0) > 0 ||
                (selectedChannelForVerification.verification.manualInvitesNeeded?.length || 0) > 0) && (
                <div>
                  {/* Calculate actual count of failed users */}
                  {(() => {
                    const missingCount = Math.max(
                      selectedChannelForVerification.verification.missing?.length || 0,
                      selectedChannelForVerification.verification.missingEmails?.length || 0,
                      selectedChannelForVerification.verification.manualInvitesNeeded?.length || 0
                    );
                    return (
                      <h4 className="font-semibold text-red-700 mb-2 flex items-center gap-2">
                        <X className="h-4 w-4" />
                        Failed to Add ({missingCount})
                      </h4>
                    );
                  })()}
                  <div className="space-y-2">
                    {/* Display all missing emails - use missingEmails if available, otherwise use manualInvitesNeeded emails */}
                    {(() => {
                      const emailsToShow = selectedChannelForVerification.verification.missingEmails?.length > 0
                        ? selectedChannelForVerification.verification.missingEmails
                        : (selectedChannelForVerification.verification.manualInvitesNeeded?.map((inv) => inv.email) || []);
                      
                      return emailsToShow.map((email: string) => {
                        // Check if this email has manual invite info
                        const manualInvite = selectedChannelForVerification.verification.manualInvitesNeeded?.find(
                          (inv) => inv.email === email
                        );
                        
                        return (
                          <div key={email} className="flex flex-col gap-2 text-sm p-3 bg-red-50 rounded border border-red-200">
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2 flex-1">
                                <X className="h-3 w-3 text-red-600 flex-shrink-0" />
                                <span className="font-medium">{email}</span>
                                {manualInvite && (
                                  <span className="text-xs text-gray-600">
                                    → {manualInvite.channelName}
                                  </span>
                                )}
                              </div>
                              {manualInvite && (
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      navigator.clipboard.writeText(email);
                                      toast.success('Email copied to clipboard!');
                                    }}
                                    className="h-7 text-xs"
                                  >
                                    <Copy className="h-3 w-3 mr-1" />
                                    Copy Email
                                  </Button>
                                  <Button
                                    variant="default"
                                    size="sm"
                                    onClick={() => window.open(manualInvite.adminInviteUrl, '_blank')}
                                    className="h-7 text-xs bg-blue-600 hover:bg-blue-700"
                                  >
                                    <ExternalLink className="h-3 w-3 mr-1" />
                                    Open Slack Admin
                                  </Button>
                                </div>
                              )}
                            </div>
                            {manualInvite && (
                              <div className="mt-2 p-2 bg-white rounded text-xs text-gray-700 border border-gray-200">
                                <p className="font-semibold mb-1">Instructions:</p>
                                <pre className="whitespace-pre-wrap font-sans text-xs">{manualInvite.instructions}</pre>
                              </div>
                            )}
                          </div>
                        );
                      });
                    })()}
                  </div>
                  
                  {/* Manual Invite Summary */}
                  {selectedChannelForVerification.verification.manualInvitesNeeded && 
                   selectedChannelForVerification.verification.manualInvitesNeeded.length > 0 && (
                    <div className="mt-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                        <AlertCircle className="h-4 w-4" />
                        Manual Invitation Required
                      </p>
                      <p className="text-sm text-blue-800 mb-3">
                        These users are not in the OutboundBuddy Slack workspace. Use the "Open Slack Admin" button above for each user to invite them manually. After they accept the invitation, add them to the channel: <strong>{selectedChannelForVerification.verification.manualInvitesNeeded[0]?.channelName || 'the channel'}</strong>
                      </p>
                      <div className="mt-2">
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => {
                            const adminUrl = selectedChannelForVerification.verification.manualInvitesNeeded?.[0]?.adminInviteUrl || 'https://outboundbuddy.slack.com/admin/invites';
                            window.open(adminUrl, '_blank');
                          }}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Open Slack Admin Invites Page
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* Fallback message if no manual invites available */}
                  {(!selectedChannelForVerification.verification.manualInvitesNeeded || 
                    selectedChannelForVerification.verification.manualInvitesNeeded.length === 0) && (
                    <div className="mt-2 p-3 bg-yellow-50 rounded text-sm text-yellow-800">
                      <p className="font-semibold">Possible reasons:</p>
                      <ul className="list-disc list-inside mt-1 space-y-1">
                        <li>User is not in the Outbound Buddy Slack workspace</li>
                        <li>Email doesn't match Slack account</li>
                        <li>User has restricted invitations</li>
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setVerificationDialogOpen(false)}>
                Close
              </Button>
              {selectedChannelForVerification.verification.missing.length > 0 && (
                <Button onClick={() => {
                  toast.info('Re-invite functionality coming soon')
                }}>
                  Re-invite Missing Members
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Add Channel Dialog */}
      {showAddDialog && showAddDialog.type && <AddChannelDialogWrapper
        showAddDialog={showAddDialog}
        productData={productData}
        clientName={client?.name}
        onAddChannel={handleAddChannel}
        onClose={() => setShowAddDialog(null)}
      />}
      
      {/* Edit Channel Dialog */}
      {editingChannel && client && (
        <Dialog open={true} onOpenChange={(open) => !open && setEditingChannel(null)}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Channel: {editingChannel.channel.name}</DialogTitle>
              <DialogDescription>
                Configure channel name and team members
              </DialogDescription>
            </DialogHeader>
            <EditChannelDialog
              channel={editingChannel.channel}
              type={editingChannel.type}
              clientId={client.id}
              productData={productData}
              teamMembers={formDataTeamMembers}
              onSave={(updatedChannel) => handleUpdateChannel(editingChannel.type, updatedChannel)}
              onCancel={() => setEditingChannel(null)}
            />
          </DialogContent>
        </Dialog>
      )}
      
      {/* Standard Channel Creation - default set only */}
        <Card>
          <CardHeader>
          <CardTitle>Create Standard Slack Channels</CardTitle>
            <CardDescription>
            Automatically create the standard Slack channels (general, positive replies, negative replies) with welcome messages. Meetings booked and website identification are created on demand from their own cards above.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
            onClick={handleCreateStandardSlackChannels}
            disabled={isCreatingStandardChannels || isLoading || !resolvedClientId || !resolvedClientName}
              className="w-full"
            >
              <Plus className="h-4 w-4 mr-2" />
            {isCreatingStandardChannels ? 'Creating Channels...' : `Create All ${DEFAULT_CHANNEL_KEYS.length} Standard Channels`}
            </Button>
          </CardContent>
        </Card>
    </div>
  )
}

// Edit Channel Dialog Component
interface EditChannelDialogProps {
  channel: SlackChannelConfig
  type: string
  clientId: string
  productData?: { products: any[], territories: Territory[] }
  teamMembers?: any[]
  onSave: (channel: SlackChannelConfig) => void
  onCancel: () => void
}

function EditChannelDialog({ channel, type, clientId, productData, teamMembers: formDataTeamMembers, onSave, onCancel }: EditChannelDialogProps) {
  const [channelName, setChannelName] = useState(channel.name)
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>(channel.team_member_ids)
  
  const handleSave = () => {
    onSave({
      ...channel,
      name: channelName,
      team_member_ids: selectedMemberIds
    })
  }
  
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Channel Name</Label>
        <Input
          value={channelName}
          onChange={(e) => setChannelName(e.target.value)}
          placeholder="#channel-name"
        />
            </div>
      
      <div className="space-y-2">
        <Label>Team Members</Label>
        <SlackChannelMemberSelector
          clientId={clientId}
          channelType={type as any}
          territoryId={channel.territory_id}
          territoryName={channel.territory_name}
          existingMemberIds={channel.team_member_ids}
          selectedMemberIds={selectedMemberIds}
          onSelectionChange={setSelectedMemberIds}
          productData={productData}
          formDataTeamMembers={formDataTeamMembers}
        />
          </div>
      
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="button" onClick={handleSave}>
          Save
        </Button>
      </div>
    </div>
  )
}
